import BenchlingConfig from 'BenchlingConfig';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';

export const TERMINAL_PROCESS_TASK_STATUS_TYPES = new Set<ProcessTaskStatusType>([
  ProcessTaskStatusType.COMPLETED,
  ProcessTaskStatusType.SKIPPED,
  ProcessTaskStatusType.INVALID,
  ProcessTaskStatusType.FAILED,
  ProcessTaskStatusType.CANCELLED,
]);

export interface PredecessorEdge {
  sourcePmeiId: string;
  targetPmeiId: string;
}

/**
 * Resolves null (taskless) PMEI statuses to PENDING or leaves them null (PLANNED).
 *
 * Split-pool: a taskless PMEI is PENDING when it has no predecessors, or all predecessors
 * have reached a terminal status.
 * Legacy: only first-method taskless PMEIs get PENDING (tasks are created asynchronously).
 */
export function resolveTasklessStatuses(
  statuses: ReadonlyMap<string, ProcessTaskStatusType | null>,
  predecessorEdges: Array<PredecessorEdge>,
  methods: Array<{id: string}>,
  pmeiMethodIds: ReadonlyMap<string, string>
): Map<string, ProcessTaskStatusType | null> {
  const resolved = new Map(statuses);

  if (BenchlingConfig.STUDIES_ENABLE_SPLIT_POOL) {
    const predecessorsByTarget = new Map<string, Array<string>>();
    for (const {sourcePmeiId, targetPmeiId} of predecessorEdges) {
      const preds = predecessorsByTarget.get(targetPmeiId) ?? [];
      preds.push(sourcePmeiId);
      predecessorsByTarget.set(targetPmeiId, preds);
    }

    for (const [pmeiId, status] of statuses) {
      if (status != null) {
        continue;
      }
      const preds = predecessorsByTarget.get(pmeiId);
      const allPredecessorsTerminal =
        preds == null ||
        preds.length === 0 ||
        preds.every((predId) => {
          const predStatus = statuses.get(predId);
          return predStatus != null && TERMINAL_PROCESS_TASK_STATUS_TYPES.has(predStatus);
        });
      if (allPredecessorsTerminal) {
        resolved.set(pmeiId, ProcessTaskStatusType.PENDING);
      }
    }
  } else {
    const firstMethodId = methods[0]?.id;
    if (firstMethodId != null) {
      for (const [pmeiId, status] of statuses) {
        if (status == null && pmeiMethodIds.get(pmeiId) === firstMethodId) {
          resolved.set(pmeiId, ProcessTaskStatusType.PENDING);
        }
      }
    }
  }

  return resolved;
}
